import React from 'react';
import { motion } from 'framer-motion';
import { BookOpenIcon, CheckCircle2Icon, GiftIcon } from 'lucide-react';
import { PreviewCarousel } from './PreviewCarousel';
import { bonuses, volumes } from '../data/offer';
import type { Volume } from '../types/content';
// Each volume maps to one of the existing accent tokens.
const accentStyles: Record<Volume['accent'], {
  border: string;
  text: string;
}> = {
  info: {
    border: 'border-info/30',
    text: 'text-info'
  },
  primary: {
    border: 'border-primary-soft/30',
    text: 'text-primary-soft'
  },
  highlight: {
    border: 'border-highlight/30',
    text: 'text-highlight'
  },
  danger: {
    border: 'border-danger-soft/30',
    text: 'text-danger-soft'
  }
};
export function ProductOffer() {
  return <>
      <section className="bg-surface px-4 py-16 sm:py-24" data-id="element-146" data-mp="fj57v9hrp24a">
        <div className="mx-auto max-w-5xl" data-id="element-147" data-mp="fjol4cwh32bz">
          <header className="text-center" data-id="element-148" data-mp="khyimu5o6f0r">
            <h2 className="font-display text-3xl font-bold text-highlight sm:text-5xl" data-id="element-149" data-mp="khq653dglbq3">
              KIT BIENESTAR ORIENTAL™
            </h2>
            <p className="mx-auto mt-4 max-w-4xl text-lg font-medium" data-id="element-150" data-mp="3gfymh3iscbdk">
              Tu colección de bienestar oriental para aprender prácticas sencillas y seguir un programa de 28 días.
            </p>
          </header>
          <h3 className="mt-12 text-center font-display text-2xl font-bold" data-id="element-151" data-mp="3qdmlw6rc6722">
            📚 3 VOLÚMENES COMPLETOS
          </h3>
          <div className="mt-7 space-y-5" data-id="element-152" data-mp="32pd3letc01fo">
            {volumes.map((volume, index) => {
            const accent = accentStyles[volume.accent];
            return <motion.article key={volume.title} initial={{
              opacity: 0,
              y: 16
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.06
            }} className={`flex gap-5 rounded-2xl border ${accent.border} bg-canvas p-5 sm:p-7`} data-id="element-153" data-mp="3bgn3royuyvtc" data-mpi={index}>
                  <BookOpenIcon className={`mt-1 h-7 w-7 shrink-0 ${accent.text}`} aria-hidden="true" data-id="element-154" data-mp="hycyytilyums" />
                  <div className="flex-1" data-id="element-155" data-mp="mx6aigec4bkb">
                    <p className={`text-xs font-bold tracking-brand ${accent.text}`} data-id="element-156" data-mp="mw4vy1y416xu">
                      {volume.label}
                    </p>
                    <h4 className="mt-1 font-display text-lg font-bold sm:text-xl" data-id="element-157" data-mp="wn9bb2rp6tt7">
                      {volume.title}
                    </h4>
                    <p className="mt-1 text-sm text-foreground-subtle sm:text-base" data-id="element-158" data-mp="3v66i58ke9d44">
                      {volume.subtitle}
                    </p>
                    <ul className="mt-4 space-y-2" data-id="element-159" data-mp="8xognmm6m843">
                      {volume.items.map(item => <li key={item} className="flex gap-2 text-sm leading-relaxed text-foreground-muted sm:text-base" data-id="element-160" data-mp="8zrgszh1thmj">
                          <CheckCircle2Icon className="mt-0.5 h-5 w-5 shrink-0 text-primary-soft" aria-hidden="true" data-id="element-161" data-mp="1kctxduj5e76p" />
                          {item}
                        </li>)}
                    </ul>
                    <p className="mt-5 border-t border-foreground/10 pt-4 text-sm sm:text-base" data-id="element-162" data-mp="39tpibqzjmoyy">
                      <span className="font-semibold text-foreground-subtle" data-id="element-163" data-mp="34v4frkwtjt7c">Resultado: </span>
                      <span className={`font-bold italic ${accent.text}`} data-id="element-164" data-mp="3045wqdkg4h0e">{volume.result}</span>
                    </p>
                  </div>
                </motion.article>;
          })}
          </div>
        </div>
      </section>
      <PreviewCarousel data-id="element-165" data-mp="2q688hq2l6k7s" />
      <section className="bg-canvas px-4 py-16 sm:py-24" data-id="element-166" data-mp="2q7jezxnf262l">
        <div className="mx-auto max-w-5xl" data-id="element-167" data-mp="2m3v6ncqyn6ga">
          <header className="text-center" data-id="element-168" data-mp="2m2tkod8eozjs">
            <GiftIcon className="mx-auto h-10 w-10 text-highlight" aria-hidden="true" data-id="element-169" data-mp="2h51qfckah1cf" />
            <h2 className="mt-3 font-display text-3xl font-bold text-highlight sm:text-5xl" data-id="element-170" data-mp="2c66ynl4cg2g6">
              5 BONOS DE REGALO
            </h2>
          </header>
          <div className="mt-10 space-y-4" data-id="element-171" data-mp="fdewb253oqbq">
            {bonuses.map(({
            number,
            title,
            detail,
            items,
            value
          }) => <article key={number} className="flex flex-col gap-4 rounded-2xl border border-highlight/20 bg-surface p-5 sm:flex-row sm:items-start sm:p-7" data-id="element-172" data-mp="346k48lssfoas">
                <span className="w-fit whitespace-nowrap rounded-lg bg-highlight/10 px-3 py-2 font-bold text-highlight" data-id="element-173" data-mp="3862nld4ynrbi">
                  🎁 BONO {number}
                </span>
                <div className="flex-1" data-id="element-174" data-mp="2z5w4nbqhtz4y">
                  <h3 className="font-bold" data-id="element-175" data-mp="2kj2tmio0bfik">{title}</h3>
                  
                  <ul className="mt-3 space-y-1.5" data-id="element-176" data-mp="2kgzv1a8sge28">
                    {items.map(item => <li key={item} className="flex gap-2 text-sm leading-relaxed text-foreground-muted" data-id="element-177" data-mp="2pb488b8ndibr">
                        <CheckCircle2Icon className="mt-0.5 h-4 w-4 shrink-0 text-highlight" aria-hidden="true" data-id="element-178" data-mp="2bjzc3j4hn866" />
                        {item}
                      </li>)}
                  </ul>
                </div>
                <div className="flex shrink-0 items-center gap-3" data-id="element-179" data-mp="21mb61x1fme8p">
                  <span className="text-xs font-semibold text-foreground-faint" data-id="element-180" data-mp="26ijr1r2b91ns">VALOR:</span>
                  <span className="text-sm text-foreground-faint line-through" data-id="element-181" data-mp="1my1eqdc92w85">
                    {value}
                  </span>
                  <span className="rounded-full bg-primary-soft px-3 py-1 text-sm font-bold text-canvas" data-id="element-182" data-mp="1ixr58qp91noj">
                    GRATIS
                  </span>
                </div>
              </article>)}
          </div>
          <div className="mt-8 rounded-2xl border border-highlight/30 bg-highlight/10 p-6 text-center" data-id="element-183" data-mp="25w46v103h9a6">
            <p className="text-xs font-bold tracking-brand text-highlight" data-id="element-184" data-mp="21usdqli0i5x8">
              VALOR TOTAL BONOS
            </p>
            <p className="mt-1 text-2xl font-bold text-foreground-subtle line-through" data-id="element-185" data-mp="2fm71f5mv6vam">
              $52.000
            </p>
            <p className="mt-2 font-display text-xl font-bold text-highlight" data-id="element-186" data-mp="2andujw2322jf">
              GRATIS con tu Kit Bienestar Oriental™
            </p>
          </div>
        </div>
      </section>
    </>;
}